import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthShell } from "@/components/layouts";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setRes((await api.post("/auth/forgot-password", { email })).data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell>
      <p className="eyebrow mb-3">Account recovery</p>
      <h1 className="text-4xl font-semibold">Forgot password</h1>
      <p className="text-sm text-muted-foreground mt-2">Enter your email to generate a reset link.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl bg-card" data-testid="forgot-email-input" /></Field>
        <Button disabled={busy} className="w-full h-12 rounded-full" data-testid="forgot-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate reset link"}</Button>
      </form>
      {res && (
        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="forgot-result">
          <p>{res.message}</p>
          {res.reset_link && <Link to={res.reset_link} className="text-primary underline mt-2 inline-block" data-testid="reset-link">Open reset link</Link>}
        </div>
      )}
      <p className="text-sm text-muted-foreground mt-8"><Link to="/login" className="text-primary hover:underline" data-testid="back-to-login-link">Back to login</Link></p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw.a });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell>
      <p className="eyebrow mb-3">Account recovery</p>
      <h1 className="text-4xl font-semibold">Set a new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="New password" hint="Minimum 8 characters"><Input type="password" minLength={8} required value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className="h-12 rounded-xl bg-card" data-testid="reset-password-input" /></Field>
        <Field label="Confirm password"><Input type="password" minLength={8} required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className="h-12 rounded-xl bg-card" data-testid="reset-password-confirm-input" /></Field>
        <Button disabled={busy} className="w-full h-12 rounded-full" data-testid="reset-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
      </form>
    </AuthShell>
  );
}
